import {
  measurePerformanceAsync,
  scanLorebooksSeeded,
  type ActivationByPosition,
  type Lorebook,
  type LorebookScanRequest,
} from "@roqeet/core";

/**
 * 로어북 스캔 호스트 — 결정 #9/#12. 무거운 키워드 스캔을 Web Worker로 오프로딩하되,
 * 작은 로어북은 clone/postMessage 왕복 비용이 더 크므로 메인스레드에서 동기 실행한다.
 * Worker가 없거나(미지원/오류) 실패하면 동기 폴백한다 — 결과는 어느 경로든 동일하다
 * (core scanLorebooksSeeded는 순수 함수).
 *
 * scanText(Roqeet 인라인 전개)는 메인스레드가 미리 만들어 넘긴다(가벼움). Worker엔 함수가 못
 * 넘으므로 시드(숫자)로 RNG 재현성을 맞춘다.
 */

/** 이 임계치 이상의 엔트리 수일 때만 Worker로 오프로딩(작은 건 동기가 빠름). */
const OFFLOAD_ENTRY_THRESHOLD = 50;

interface PendingResolver {
  resolve: (a: ActivationByPosition) => void;
  reject: (e: unknown) => void;
}

export class LorebookScanHost {
  private worker: Worker | null = null;
  private workerFailed = false;
  private seq = 0;
  private readonly pending = new Map<number, PendingResolver>();

  /** 총 엔트리 수를 센다(오프로딩 가치 판단용). */
  private totalEntries(lorebooks: Lorebook[]): number {
    let n = 0;
    for (const b of lorebooks) n += b.entries.length;
    return n;
  }

  /** Worker를 lazy 생성한다. 생성 실패 시 null(이후 동기 폴백). */
  private getWorker(): Worker | null {
    if (this.worker || this.workerFailed) return this.worker;
    try {
      // Vite가 이 형태를 보고 Worker 청크를 분리 번들한다(lazy).
      this.worker = new Worker(new URL("./scan-worker.ts", import.meta.url), { type: "module" });
      this.worker.onmessage = (e: MessageEvent<{ id: number; activation: ActivationByPosition }>) => {
        const { id, activation } = e.data;
        const p = this.pending.get(id);
        if (p) {
          this.pending.delete(id);
          p.resolve(activation);
        }
      };
      this.worker.onerror = () => {
        // Worker 자체 오류: 이후 전부 동기 폴백 + 대기 중인 것 거부.
        this.workerFailed = true;
        for (const [, p] of this.pending) p.reject(new Error("scan worker error"));
        this.pending.clear();
        this.worker?.terminate();
        this.worker = null;
      };
    } catch {
      this.workerFailed = true;
      this.worker = null;
    }
    return this.worker;
  }

  /**
   * 로어북 활성화를 계산한다(결정 #12). 큰 로어북이면 Worker로, 작으면 동기로, Worker
   * 실패 시 동기로 폴백한다. 어느 경로든 결과는 동일하다.
   *
   * @param scanText Roqeet 인라인 전개된 스캔 텍스트(메인스레드에서 buildLorebookScanText로 생성).
   * @param seed     확률 게이트용 시드(재현성). 같은 대화/상태면 같은 시드를 주입한다.
   */
  async scan(
    lorebooks: Lorebook[],
    scanText: string,
    seed: number,
  ): Promise<ActivationByPosition> {
    const req: LorebookScanRequest = { lorebooks, scanText, seed };
    const totalEntries = this.totalEntries(lorebooks);

    // 작은 로어북 또는 Worker 미지원 → 동기(왕복 비용 회피).
    if (
      totalEntries < OFFLOAD_ENTRY_THRESHOLD ||
      typeof Worker === "undefined"
    ) {
      return measurePerformanceAsync(
        "retrieve_memory",
        {
          inputSize: scanText.length,
          fileId: "lorebook-scan-host",
          packageId: lorebooks.map((book) => book.id).join(","),
          cache: "bypass",
          thread: "main",
          invalidationReason: "below_worker_threshold",
          detail: { entries: totalEntries },
        },
        async () => scanLorebooksSeeded(req),
      );
    }

    const worker = this.getWorker();
    if (!worker) {
      return measurePerformanceAsync(
        "retrieve_memory",
        {
          inputSize: scanText.length,
          fileId: "lorebook-scan-host",
          packageId: lorebooks.map((book) => book.id).join(","),
          cache: "bypass",
          thread: "main",
          invalidationReason: "worker_unavailable",
          detail: { entries: totalEntries },
        },
        async () => scanLorebooksSeeded(req),
      );
    }
    const id = ++this.seq;
    try {
      return await measurePerformanceAsync(
        "retrieve_memory",
        {
          inputSize: scanText.length,
          fileId: "lorebook-scan-host",
          packageId: lorebooks.map((book) => book.id).join(","),
          cache: "bypass",
          thread: "worker",
          invalidationReason: "scan_text_or_lorebook_changed",
          detail: { entries: totalEntries },
        },
        () =>
          new Promise<ActivationByPosition>((resolve, reject) => {
            this.pending.set(id, { resolve, reject });
            worker.postMessage({ id, req });
          }),
      );
    } catch {
      // Worker 경로 실패 → 동기 폴백(결과 동일).
      return measurePerformanceAsync(
        "retrieve_memory",
        {
          inputSize: scanText.length,
          fileId: "lorebook-scan-host",
          packageId: lorebooks.map((book) => book.id).join(","),
          cache: "bypass",
          thread: "main",
          invalidationReason: "worker_error_fallback",
          detail: { entries: totalEntries },
        },
        async () => scanLorebooksSeeded(req),
      );
    }
  }

  dispose(): void {
    this.worker?.terminate();
    this.worker = null;
    this.pending.clear();
  }
}
